import { useState } from "react";
import { execService } from "@/services";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, Loader2, ChevronDown, ChevronRight } from "lucide-react";
import { fmtValue } from "./shared";

export function AiExecutiveSummary({ period, params }) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showSrc, setShowSrc] = useState(false);

  const generate = async () => {
    setLoading(true);
    try {
      const r = await execService.aiSummary({ period, tenantId: params.tenantId });
      setResult(r);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="bg-card border border-border rounded-xl p-5" data-testid="ai-summary-panel">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" /> AI Executive Summary</h2>
        <button onClick={generate} disabled={loading} className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-xs font-semibold hover:bg-primary/90 disabled:opacity-50" data-testid="ai-summary-generate">
          {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} {result ? "Regenerate" : "Generate summary"}
        </button>
      </div>
      {!result && !loading && <p className="text-xs text-muted-foreground">Generate an AI briefing grounded only on this tenant's live KPIs (gpt-5.2).</p>}
      {loading && <p className="text-xs text-muted-foreground">Analysing KPIs…</p>}
      {result && (
        <>
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-line" data-testid="ai-summary-text">{result.summary}</p>
          <div className="flex items-center gap-2 mt-3">
            <span className="text-[11px] text-muted-foreground">{result.model} · confidence {result.label}</span>
            <button onClick={() => setShowSrc(!showSrc)} className="text-[11px] text-primary font-semibold inline-flex items-center gap-0.5" data-testid="ai-summary-sources-toggle">
              {showSrc ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />} Source context ({result.sourceContext?.length || 0})
            </button>
          </div>
          {showSrc && (
            <div className="mt-2 bg-muted/40 rounded-lg p-3 grid sm:grid-cols-2 gap-x-4 gap-y-1" data-testid="ai-summary-sources">
              {(result.sourceContext || []).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-[11px]">
                  <span className="text-muted-foreground capitalize">{s.pillar} · {s.label}</span>
                  <span className="font-semibold text-foreground">{s.format ? fmtValue(s.value, s.format) : s.value}</span>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
